// Aviso do iPhone nas notificações (Pedro, 31/10/2026, T-0007): o passo muda com o jeito que o iPhone abriu o painel
// (Instagram, Facebook, Safari do iOS 26, Safari 16.4–18, Chrome, iOS antigo); os textos e a classificação são os de
// @workspace/text (os mesmos que a Atendente lê). Aqui: o aviso das Configurações, a faixa do topo de Pedidos e o
// registro de como a loja abre o painel (POST /api/push/painel-iphone, no máximo um por dia ou quando muda).
import { useEffect, useState } from "react";
import { useLocation } from "wouter";
import { BellOff, BellRing, Check, Copy, X } from "lucide-react";
import { customFetch } from "@workspace/api-client-react";
import { avisoDoPainelIphone, tipoDoPainelIphone, type TipoDoPainelIphone } from "@workspace/text";

export type NotificacaoDoIphone = "ativa" | "desligada" | "bloqueada" | "sem_suporte";

function aberturaComoApp(): boolean {
  if ((navigator as { standalone?: boolean }).standalone === true) return true;
  try {
    return window.matchMedia("(display-mode: standalone)").matches;
  } catch {
    return false;
  }
}

export function tipoDesteIphone(): TipoDoPainelIphone | null {
  if (typeof navigator === "undefined") return null;
  return tipoDoPainelIphone(navigator.userAgent || "", { standalone: aberturaComoApp(), maxTouchPoints: navigator.maxTouchPoints });
}

async function notificacaoDesteIphone(tipo: TipoDoPainelIphone): Promise<NotificacaoDoIphone> {
  if (tipo !== "icone" || !("serviceWorker" in navigator) || !("PushManager" in window) || typeof Notification === "undefined") return "sem_suporte";
  if (Notification.permission === "denied") return "bloqueada";
  if (Notification.permission !== "granted") return "desligada";
  try {
    const reg = await navigator.serviceWorker.getRegistration();
    return (await reg?.pushManager.getSubscription()) ? "ativa" : "desligada";
  } catch {
    return "desligada";
  }
}

const CHAVE_DO_REGISTRO = "vnz-painel-iphone";
const CHAVE_DA_FAIXA = "vnz-faixa-iphone-agora-nao";
const DIAS_DO_AGORA_NAO = 7;

/** Grava como a loja abre o painel no iPhone (para a Atendente), no máximo uma vez por dia ou quando muda. */
export function useRegistrarPainelIphone(ligado: boolean) {
  useEffect(() => {
    if (!ligado) return;
    const tipo = tipoDesteIphone();
    if (!tipo) return;
    let cancelado = false;
    void notificacaoDesteIphone(tipo).then((notificacao) => {
      if (cancelado) return;
      const marca = `${tipo}|${notificacao}|${new Date().toISOString().slice(0, 10)}`;
      try {
        if (localStorage.getItem(CHAVE_DO_REGISTRO) === marca) return;
      } catch {}
      customFetch("/api/push/painel-iphone", { method: "POST", body: JSON.stringify({ tipo, notificacao }) })
        .then(() => {
          try {
            localStorage.setItem(CHAVE_DO_REGISTRO, marca);
          } catch {}
        })
        .catch(() => {});
    });
    return () => {
      cancelado = true;
    };
  }, [ligado]);
}

/** O aviso amarelo das Configurações → Notificações Push, do jeito que este iPhone abriu o painel. */
export function AvisoDoIphone({ tipo }: { tipo: TipoDoPainelIphone }) {
  const aviso = avisoDoPainelIphone(tipo);
  const [copiou, setCopiou] = useState<"sim" | "falhou" | null>(null);
  if (!aviso) return null;
  const link = `${window.location.origin}${import.meta.env.BASE_URL.replace(/\/$/, "")}/configuracoes#cfg-notificacoes`;
  const copiar = async () => {
    try {
      await navigator.clipboard.writeText(link);
      setCopiou("sim");
    } catch {
      // O navegador de dentro do Instagram às vezes recusa: o link aparece para copiar à mão.
      setCopiou("falhou");
    }
  };
  return (
    <div className="space-y-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800 dark:border-amber-800 dark:bg-amber-950/20 dark:text-amber-300" data-testid="aviso-iphone" data-tipo={tipo}>
      <div className="flex items-center gap-2 font-medium">
        <BellOff className="h-4 w-4 shrink-0" />
        {aviso.titulo}
      </div>
      {aviso.texto && <p>{aviso.texto}</p>}
      {aviso.passos.length > 0 && (
        <ol className="list-decimal space-y-1 pl-5" data-testid="aviso-iphone-passos">
          {aviso.passos.map((p) => (
            <li key={p}>{p}</li>
          ))}
        </ol>
      )}
      {aviso.copiarLink && (
        <div className="space-y-2">
          <button
            type="button"
            onClick={() => void copiar()}
            className="inline-flex h-9 items-center gap-1.5 rounded-lg bg-amber-600 px-3 text-sm font-semibold text-white hover:bg-amber-700"
            data-testid="aviso-iphone-copiar"
          >
            {copiou === "sim" ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
            {copiou === "sim" ? "Link copiado" : "Copiar link"}
          </button>
          {copiou === "falhou" && (
            <div className="space-y-1">
              <p className="text-xs">Não deu para copiar sozinho. Segure no link abaixo e copie:</p>
              <input
                readOnly
                value={link}
                onFocus={(e) => e.currentTarget.select()}
                className="w-full rounded-md border border-amber-300 bg-white px-2 py-1.5 text-xs text-foreground dark:bg-background"
                data-testid="aviso-iphone-link"
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * A faixa do topo de Pedidos: iPhone sem notificação (fora do ícone, ou no ícone sem ativar). «Ver como» leva ao aviso
 * das Configurações; «Agora não» some por 7 dias neste aparelho. Não aparece com a notificação bloqueada (o caminho é
 * outro) nem no iOS antigo (não tem como).
 */
export function FaixaDoIphone() {
  const [, navegar] = useLocation();
  const [mostrar, setMostrar] = useState(false);
  useEffect(() => {
    const tipo = tipoDesteIphone();
    if (!tipo || tipo === "ios_antigo") return;
    try {
      const ate = Number(localStorage.getItem(CHAVE_DA_FAIXA) || 0);
      if (ate > Date.now()) return;
    } catch {}
    let cancelado = false;
    void notificacaoDesteIphone(tipo).then((n) => {
      if (!cancelado && (n === "sem_suporte" || n === "desligada")) setMostrar(true);
    });
    return () => {
      cancelado = true;
    };
  }, []);
  if (!mostrar) return null;
  const agoraNao = () => {
    try {
      localStorage.setItem(CHAVE_DA_FAIXA, String(Date.now() + DIAS_DO_AGORA_NAO * 86_400_000));
    } catch {}
    setMostrar(false);
  };
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-2 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2.5 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950/20 dark:text-amber-200" data-testid="faixa-iphone">
      <BellRing className="h-4 w-4 shrink-0" />
      <span className="min-w-0 flex-1 font-medium">Receba aviso de pedido novo neste iPhone</span>
      <div className="flex shrink-0 items-center gap-2">
        <button type="button" onClick={() => navegar("/configuracoes#cfg-notificacoes")} className="h-8 rounded-lg bg-amber-600 px-3 text-xs font-semibold text-white hover:bg-amber-700" data-testid="faixa-iphone-ver">
          Ver como
        </button>
        <button type="button" onClick={agoraNao} className="flex h-8 items-center gap-1 rounded-lg px-2 text-xs font-medium hover:bg-amber-100 dark:hover:bg-amber-900/40" data-testid="faixa-iphone-agora-nao">
          <X className="h-3.5 w-3.5" />
          Agora não
        </button>
      </div>
    </div>
  );
}
